/**
 * OmniClinicalBridge Offline Data Sync Manager
 * OmniAlera Technologies Holdings Inc.
 * 
 * Manages synchronization of clinical data for offline use
 * Ensures healthcare providers have access to critical information
 * even in areas with limited or no connectivity
 */

import { 
  initDB, 
  putMany, 
  getAll, 
  hasData, 
  STORES,
  put,
  getStorageEstimate
} from './indexedDB';

// Import your data (adjust paths as needed)
// These would be imported from your actual data files
// import { enhancedMedicines } from '@/data/enhancedMedicines';
// import { clinicalConditions } from '@/data/clinicalConditions';
// import { calculators } from '@/data/calculators';

interface SyncStatus {
  medicines: boolean;
  conditions: boolean;
  calculators: boolean;
  lastSync: Date | null;
  error: string | null;
}

interface SyncProgress {
  current: number;
  total: number;
  stage: 'medicines' | 'conditions' | 'calculators' | 'complete';
}

type ProgressCallback = (progress: SyncProgress) => void;

class OfflineSyncManager {
  private syncStatus: SyncStatus = {
    medicines: false,
    conditions: false,
    calculators: false,
    lastSync: null,
    error: null
  };
  
  private isSyncing = false;
  
  /**
   * Initialize the offline sync manager
   */
  async initialize(): Promise<void> {
    await initDB();
    await this.checkSyncStatus();
  }
  
  /**
   * Check current sync status from IndexedDB
   */
  async checkSyncStatus(): Promise<SyncStatus> {
    try {
      this.syncStatus.medicines = await hasData(STORES.MEDICINES);
      this.syncStatus.conditions = await hasData(STORES.CONDITIONS);
      this.syncStatus.calculators = await hasData(STORES.CALCULATORS);
      
      // Get last sync time from preferences
      const lastSyncStr = localStorage.getItem('omniclinicalbridge_last_sync');
      this.syncStatus.lastSync = lastSyncStr ? new Date(lastSyncStr) : null;
      
      this.syncStatus.error = null;
    } catch (error) {
      this.syncStatus.error = error instanceof Error ? error.message : 'Unknown error';
    }
    
    return this.syncStatus;
  }
  
  /**
   * Sync all clinical data for offline use
   */
  async syncAllData(
    medicines: any[],
    conditions: any[],
    calculators: any[],
    onProgress?: ProgressCallback
  ): Promise<boolean> {
    if (this.isSyncing) {
      console.warn('[OfflineSync] Sync already in progress');
      return false;
    }
    
    this.isSyncing = true;
    const total = medicines.length + conditions.length + calculators.length;
    let current = 0;
    
    try {
      // Sync medicines
      onProgress?.({ current, total, stage: 'medicines' });
      await putMany(STORES.MEDICINES, medicines);
      current += medicines.length;
      console.log(`[OfflineSync] Synced ${medicines.length} medicines`);
      
      // Sync conditions
      onProgress?.({ current, total, stage: 'conditions' });
      await putMany(STORES.CONDITIONS, conditions);
      current += conditions.length;
      console.log(`[OfflineSync] Synced ${conditions.length} conditions`);
      
      // Sync calculators
      onProgress?.({ current, total, stage: 'calculators' });
      await putMany(STORES.CALCULATORS, calculators);
      current += calculators.length;
      console.log(`[OfflineSync] Synced ${calculators.length} calculators`);
      
      // Update sync status
      const now = new Date();
      localStorage.setItem('omniclinicalbridge_last_sync', now.toISOString());
      
      this.syncStatus = {
        medicines: true,
        conditions: true,
        calculators: true,
        lastSync: now,
        error: null
      };
      
      onProgress?.({ current: total, total, stage: 'complete' });
      
      console.log('[OfflineSync] All data synced successfully');
      return true;
      
    } catch (error) {
      this.syncStatus.error = error instanceof Error ? error.message : 'Sync failed';
      console.error('[OfflineSync] Sync failed:', error);
      return false;
      
    } finally {
      this.isSyncing = false;
    }
  }
  
  /**
   * Get medicines from offline cache
   */
  async getMedicines<T>(): Promise<T[]> {
    return getAll<T>(STORES.MEDICINES);
  }
  
  /**
   * Get conditions from offline cache
   */
  async getConditions<T>(): Promise<T[]> {
    return getAll<T>(STORES.CONDITIONS);
  }
  
  /**
   * Get calculators from offline cache
   */
  async getCalculators<T>(): Promise<T[]> {
    return getAll<T>(STORES.CALCULATORS);
  }
  
  /**
   * Check if device is online
   */
  isOnline(): boolean {
    return navigator.onLine;
  }
  
  /**
   * Check if all data is synced for offline use
   */
  isFullySynced(): boolean {
    return this.syncStatus.medicines && 
           this.syncStatus.conditions && 
           this.syncStatus.calculators;
  }
  
  /**
   * Get current sync status
   */
  getSyncStatus(): SyncStatus {
    return { ...this.syncStatus };
  }
  
  /**
   * Get storage usage information
   */
  async getStorageInfo(): Promise<{
    used: string;
    available: string;
    percentage: number;
  } | null> {
    const estimate = await getStorageEstimate();
    if (!estimate) return null;
    
    const usedMB = (estimate.usage / (1024 * 1024)).toFixed(2);
    const quotaMB = (estimate.quota / (1024 * 1024)).toFixed(2);
    const percentage = Math.round((estimate.usage / estimate.quota) * 100);
    
    return {
      used: `${usedMB} MB`,
      available: `${quotaMB} MB`,
      percentage
    };
  }
  
  /**
   * Add item to offline queue for later sync
   */
  async queueForSync(action: {
    type: 'favorite' | 'search' | 'audit';
    data: any;
  }): Promise<void> {
    await put(STORES.OFFLINE_QUEUE, {
      ...action,
      timestamp: new Date().toISOString(),
      synced: false
    });
  }
  
  /**
   * Process offline queue when back online
   */
  async processOfflineQueue(): Promise<void> {
    if (!this.isOnline()) return;
    
    const queue = await getAll<any>(STORES.OFFLINE_QUEUE);
    
    for (const item of queue) {
      try {
        // Process each queued item based on type
        // This would integrate with your backend API
        console.log('[OfflineSync] Processing queued item:', item.type);
        // await api.syncItem(item);
        
        // Mark as synced or remove from queue
        // await remove(STORES.OFFLINE_QUEUE, item.id);
      } catch (error) {
        console.error('[OfflineSync] Failed to process queued item:', error);
      }
    }
  }
}

// Export singleton instance
export const offlineSyncManager = new OfflineSyncManager();

// Auto-process queue when coming back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    offlineSyncManager.processOfflineQueue();
  });
}
